import type {
  CustomAnswers,
  CustomAnswerValue,
  CustomQuestion,
  CustomQuestionType,
  SchedulingError,
} from '@/lib/api/types/scheduling';

export const CUSTOM_FIELD_PREFIX = 'custom';

export function customFieldKey(key: string): string {
  return `${CUSTOM_FIELD_PREFIX}:${key}`;
}

export function initialAnswerValue(
  type: CustomQuestionType
): CustomAnswerValue {
  switch (type) {
    case 'multi_select':
      return [];
    case 'checkbox':
      return false;
    default:
      return '';
  }
}

export function isAnswerEmpty(
  value: CustomAnswerValue,
  type: CustomQuestionType
): boolean {
  if (type === 'checkbox') {
    return value !== true;
  }
  if (type === 'multi_select') {
    return !Array.isArray(value) || value.length === 0;
  }
  return typeof value !== 'string' || value.trim() === '';
}

export function seedAnswers(
  questions: CustomQuestion[]
): Record<string, CustomAnswerValue> {
  const answers: Record<string, CustomAnswerValue> = {};
  for (const question of questions) {
    if (!Object.hasOwn(answers, question.key)) {
      answers[question.key] = initialAnswerValue(question.type);
    }
  }
  return answers;
}

export function buildCustomAnswersPayload(
  questions: CustomQuestion[],
  answers: Record<string, CustomAnswerValue>
): CustomAnswers {
  const payload: CustomAnswers = {};
  for (const question of questions) {
    const value = Object.hasOwn(answers, question.key)
      ? answers[question.key]
      : initialAnswerValue(question.type);
    payload[question.key] = typeof value === 'string' ? value.trim() : value;
  }
  return payload;
}

export function parseCustomAnswerErrors(
  response: unknown,
  questions: CustomQuestion[]
): string[] {
  if (
    typeof response !== 'object' ||
    response === null ||
    Array.isArray(response)
  ) {
    return [];
  }
  const customAnswerErrors = (response as SchedulingError).custom_answers;
  if (
    typeof customAnswerErrors !== 'object' ||
    customAnswerErrors === null ||
    Array.isArray(customAnswerErrors)
  ) {
    return [];
  }
  const errorKeys = Object.keys(customAnswerErrors);
  const knownKeys = new Set(questions.map(question => question.key));
  return errorKeys.filter(key => knownKeys.has(key));
}
